import React from 'react';
import {
  Compass,
  Cpu,
  Code,
  CircuitBoard,
  Zap,
  Layers,
  ShieldCheck,
  RefreshCw,
  Activity,
  Settings,
  Factory,
  Gauge,
  Wrench,
  Flame,
  Boxes,
  Network,
  Bot,
  Wind,
  Cable,
  Server,
  Sliders,
  TrendingUp,
  Globe,
  Award,
  LucideProps,
} from 'lucide-react';

export interface ServiceIconDefinition {
  id: string;
  name: string;
  desc: string;
  category: 'Architecture' | 'Hardware' | 'Software' | 'Power' | 'Safety' | 'Diagnostics' | 'Plant';
}

export const SERVICE_ICONS_LIBRARY: ServiceIconDefinition[] = [
  { id: 'Compass', name: 'System Compass', desc: 'System Architecture & FDS Planning', category: 'Architecture' },
  { id: 'Cpu', name: 'CPU Controller', desc: 'PLC Logic & Industrial Controllers', category: 'Hardware' },
  { id: 'Code', name: 'Code / SCADA', desc: 'TIA Portal V19 & WinCC Unified SCADA', category: 'Software' },
  { id: 'CircuitBoard', name: 'Circuit Board', desc: 'Industrial Electronics & Bus I/O', category: 'Hardware' },
  { id: 'Zap', name: 'Electric Zap', desc: 'Sinamics Multi-Axis Drives & Energy', category: 'Power' },
  { id: 'Layers', name: 'Control Layers', desc: 'Control Panel Assembly & MCC Cabinets', category: 'Hardware' },
  { id: 'ShieldCheck', name: 'Safety Shield', desc: 'Fail-Safe SIL3 & Functional Safety', category: 'Safety' },
  { id: 'RefreshCw', name: 'Retrofit Cycle', desc: 'Legacy Migration & S5-to-S7 Retrofits', category: 'Software' },
  { id: 'Activity', name: 'Live Telemetry', desc: 'Process Diagnostics & High-Speed Trends', category: 'Diagnostics' },
  { id: 'Settings', name: 'Settings Gear', desc: 'Field Instrumentation & Sensor Tuning', category: 'Diagnostics' },
  { id: 'Factory', name: 'Plant Factory', desc: 'Heavy Process Plants & Manufacturing', category: 'Plant' },
  { id: 'Gauge', name: 'Motion Gauge', desc: 'Line Speed & Pressure Instrumentation', category: 'Diagnostics' },
  { id: 'Wrench', name: 'Service Wrench', desc: 'Site Erection, Wiring & Commissioning', category: 'Hardware' },
  { id: 'Flame', name: 'Thermal Flame', desc: 'Furnaces, Kilns & Boiler Control', category: 'Plant' },
  { id: 'Boxes', name: 'Material Boxes', desc: 'Conveyors, Sortation & Palletizers', category: 'Plant' },
  { id: 'Network', name: 'Profinet Bus', desc: 'Profinet, Modbus & Industrial Ethernet', category: 'Software' },
  { id: 'Bot', name: 'Robotics Bot', desc: 'Articulated Arms & Pick-and-Place Cells', category: 'Plant' },
  { id: 'Wind', name: 'Clean Airflow', desc: 'HVAC, Cleanroom & Exhaust Pneumatics', category: 'Plant' },
  { id: 'Cable', name: 'Field Cabling', desc: 'Multi-Core Cable Trays & Panel Marshalling', category: 'Hardware' },
  { id: 'Server', name: 'Edge IPC Server', desc: 'Industrial PCs & OT Virtualization', category: 'Software' },
  { id: 'Sliders', name: 'PID Sliders', desc: 'Closed-Loop PID Tuning & Parameterization', category: 'Diagnostics' },
  { id: 'TrendingUp', name: 'Performance OEE', desc: 'Production Yield & OEE Optimization', category: 'Diagnostics' },
  { id: 'Globe', name: 'Global Globe', desc: 'Worldwide Commissioning & Remote Support', category: 'Architecture' },
  { id: 'Award', name: 'ISO Compliance', desc: 'ISO 9001:2015 Quality & Validation', category: 'Safety' },
];

export interface ServiceIconProps extends React.SVGProps<SVGSVGElement> {
  size?: string | number;
  color?: string;
  strokeWidth?: string | number;
  className?: string;
  style?: React.CSSProperties;
}

const ICONS_MAP: Record<string, React.ComponentType<any>> = {
  Compass,
  Cpu,
  Code,
  CircuitBoard,
  Zap,
  Layers,
  ShieldCheck,
  RefreshCw,
  Activity,
  Settings,
  Factory,
  Gauge,
  Wrench,
  Flame,
  Boxes,
  Network,
  Bot,
  Wind,
  Cable,
  Server,
  Sliders,
  TrendingUp,
  Globe,
  Award,
};

const NORMALIZED_ICONS_MAP: Record<string, React.ComponentType<any>> = {};
Object.entries(ICONS_MAP).forEach(([key, comp]) => {
  NORMALIZED_ICONS_MAP[key.toLowerCase()] = comp;
  NORMALIZED_ICONS_MAP[key.toLowerCase().replace(/[^a-z0-9]/g, '')] = comp;
});

export const renderServiceIconById = (
  iconId: string,
  props: ServiceIconProps = {}
): React.ReactElement => {
  const cleanId = (iconId || '').trim();
  const normalizedKey = cleanId.toLowerCase().replace(/[^a-z0-9]/g, '');
  const Component = ICONS_MAP[cleanId] || NORMALIZED_ICONS_MAP[cleanId.toLowerCase()] || NORMALIZED_ICONS_MAP[normalizedKey] || CircuitBoard;
  const effectiveStrokeWidth =
    props.strokeWidth !== undefined && props.strokeWidth !== ''
      ? props.strokeWidth
      : 1;

  // Clean out any conflicting heavier stroke-width Tailwind classes (e.g. stroke-[1.5], stroke-[2], stroke-2)
  const rawClassName = props.className || '';
  const sanitizedClassName = rawClassName
    .replace(/\bstroke-\[[^\]]+\]/g, '')
    .replace(/\bstroke-[1-9]\b/g, '')
    .trim();

  const strokeClass = `stroke-[${effectiveStrokeWidth}]`;
  const mergedClassName = sanitizedClassName
    ? `${sanitizedClassName} ${strokeClass}`
    : strokeClass;

  const mergedProps: ServiceIconProps = {
    ...props,
    strokeWidth: effectiveStrokeWidth,
    className: mergedClassName,
    style: {
      strokeWidth:
        typeof effectiveStrokeWidth === 'number'
          ? `${effectiveStrokeWidth}px`
          : effectiveStrokeWidth,
      ...props.style,
    },
  };
  return React.createElement(Component, mergedProps);
};

